import React from 'react';
import moment from 'moment/moment';
import {Tag} from 'antd';
import InvoiceStatusMark from '../../components/pages/InvoicesPage/InvoiceStatusMark';
import StatusLine from '../../components/pages/SubscriptionsPage/StatusLine';
import {
  AddInvoiceIcon,
  Invoice2Icon,
  InvoicePendingIcon,
  InvoiceSuggestIcon,
  TransactionDeclinedIcon,
} from '../../icons';
import {objectHelpers} from './object.helpers';
import {OptionsList} from './form.helpers';
import {reviewHelpers} from './review.helpers';
import {
  expenseTypesConstants,
  invoiceApprovalStatusConstants,
  invoiceMatchTypeConstants,
  invoicePaymentStatusConstants,
  invoiceStatusConstants, invoiceVatTypeConstants
} from '../../constants';
import routes from '../../routes/routes.json';

const gObjProp = objectHelpers.getObjProp;

const {MATCHED, PENDING} = invoiceStatusConstants;

const {AUTOMATED} = invoiceMatchTypeConstants;

const invoiceApprovalStatuses = {
  [invoiceApprovalStatusConstants.ASSIGNED]: {
    color: '#00465D',
    backgroundColor: '#C6E7F2',
    key: 'invoiceApprovalStatuses.assigned'
  },
  [invoiceApprovalStatusConstants.APPROVED]: {
    color: '#245F07',
    backgroundColor: '#D6FFCE',
    key: 'invoiceApprovalStatuses.approved'
  },
  [invoiceApprovalStatusConstants.PENDING]: {
    color: '#7A2E0E',
    backgroundColor: '#FEF0C7',
    key: 'invoiceApprovalStatuses.inReview'
  },
  [invoiceApprovalStatusConstants.REJECTED]: {
    color: '#D92D20',
    backgroundColor: '#FEE4E2',
    key: 'invoiceApprovalStatuses.rejected'
  },
  [invoiceApprovalStatusConstants.UNASSIGNED]: {
    color: '#9CA1AA',
    backgroundColor: '#F3F3F3',
    key: 'invoiceApprovalStatuses.unassigned'
  },
};

const invoicePaymentStatuses = {
  [invoicePaymentStatusConstants.DUE]: {
    key: 'invoiceStatuses.due',
    color: '#FFCC00'
  },
  [invoicePaymentStatusConstants.OVERDUE]: {
    key: 'invoiceStatuses.overdue',
    color: '#FF4D4F'
  },
  [invoicePaymentStatusConstants.PAID]: {
    key: 'invoiceStatuses.paid',
    color: '#52C41A'
  },
  [invoicePaymentStatusConstants.PAID_OUTSIDE]: {
    key: 'invoiceStatuses.paid',
    color: '#52C41A'
  },
  [invoicePaymentStatusConstants.PROCESSING]: {
    key: 'invoiceStatuses.processing',
    color: '#CDD0D5'
  },
  [invoicePaymentStatusConstants.UPCOMING]: {
    key: 'invoiceStatuses.upcoming',
    color: '#CDD0D5'
  },
};

const invoiceVatTypes = {
  [invoiceVatTypeConstants.STANDARD]: 'invoiceVatTypes.standard',
  [invoiceVatTypeConstants.EXEMPT]: 'invoiceVatTypes.exempt',
  [invoiceVatTypeConstants.REDUCED]: 'invoiceVatTypes.reduced',
  [invoiceVatTypeConstants.SUPER_REDUCED]: 'invoiceVatTypes.superReduced',
};

export const InvoiceVatOptions = () => OptionsList(invoiceVatTypes);

export const InvoicePaymentStatusesOptions = ({ excludePaidOutsideProcessing = false } = {}) => {
  const statuses = Object.keys(invoicePaymentStatuses).reduce((acc, value) => {
    if (
      excludePaidOutsideProcessing &&
      (String(value) === String(invoicePaymentStatusConstants.PAID_OUTSIDE) ||
       String(value) === String(invoicePaymentStatusConstants.PROCESSING))
    ) {
      return acc;
    }
    acc[value] = invoicePaymentStatuses[value]?.key;
    return acc;
  }, {});
  return OptionsList(statuses).map(option => ({
    ...option,
    image: <StatusLine color={invoicePaymentStatuses[option.value]?.color} />
  }));
};


export const invoicesHelpers = {
  convertInvoiceToTransaction: (invoice) => {
    if (!invoice) return null;

    return {
      amount: invoice?.amount || 0,
      created_date: invoice?.date,
      source: invoice?.vendor
    }
  },
  convertMatchFilterPropsToApi: (input, { dateGteKey = 'date_gte', dateLteKey = 'date_lte', amountLteKey = 'amount_lte', amountGteKey = 'amount_gte' } = {}) => {
    const output = {};
    const dateFormat = 'YYYY-MM-DD';

    // Convert amountType
    if (input.amountType && input.amount) {
      const {amount, amountType} = input;
      const amountMapping = {
        '>': amountLteKey,
        '<': amountGteKey
      };
      if (amountMapping[amountType]) {
        output[amountMapping[amountType]] = amount;
      } else if (amountType === '=') {
        output[amountLteKey] = amount;
        output[amountGteKey] = amount;
      }
    }

    // Convert dateRange (use configurable keys, defaulting to date_gte/date_lte)
    if (input.dateRange && Array.isArray(input.dateRange) && input.dateRange.length === 2) {
      output[dateGteKey] = input.dateRange[0].format(dateFormat);
      output[dateLteKey] = input.dateRange[1].format(dateFormat);
    }

    // Add other input values as they are, excluding amountType and dateRange
    Object.keys(input).forEach(key => {
      if (!['amount', 'amountType', 'dateRange'].includes(key)) {
        output[key] = input[key];
      }
    });

    return output;
  },
  hasInvoiceParseError: (invoice) => {
    const threeDaysAgoDate = moment().subtract(3, 'days').startOf('day');

    if (gObjProp(invoice, 'has_parse_errors') === true) {
      return true;
    }

    if (gObjProp(invoice, 'status') === PENDING) {
      const createdAt = gObjProp(invoice, 'created_at');
      return moment(createdAt).startOf('day').isBefore(threeDaysAgoDate);
    }

    return false;
  },
  getInvoiceMatchType: (invoice) => {
    const matchType = gObjProp(invoice, 'match_type');
    const status = gObjProp(invoice, 'status');
    const hasParseErrors = invoicesHelpers.hasInvoiceParseError(invoice);

    if (hasParseErrors) {
      return <div className='invoice-match'><TransactionDeclinedIcon /></div>;
    }

    let matchElement;

    switch (status) {
      case MATCHED:
        matchElement = matchType === AUTOMATED ? <InvoiceSuggestIcon /> : <Invoice2Icon />;
        break;
      case PENDING:
        matchElement = <InvoicePendingIcon />;
        break;
      default:
        matchElement = matchType === AUTOMATED ? <InvoiceSuggestIcon /> : <AddInvoiceIcon />;
        break;
    }

    return <div className='invoice-match'>{matchElement}</div>;
  },
  getInvoicePaymentStatus: (status) => objectHelpers.getStatus(status, invoicePaymentStatuses),

  getInvoiceStatusMark: (status) => objectHelpers.isNaV(status) ? '' : <InvoiceStatusMark statusCode={status} className='invoice-status-mark' />,

  getInvoiceApprovalStatusTag: (invoice, t) => {
    if (!invoice) return null;
    let approvalStatus = invoice?.approval_status;
    const budgetRequestId = invoice?.budget_request_id;

    // show the assigned invoice status when the invoice is unassigned and a budget request exists
    if (budgetRequestId && approvalStatus === invoiceApprovalStatusConstants.UNASSIGNED) {
      approvalStatus = invoiceApprovalStatusConstants.ASSIGNED;
    }
    return reviewHelpers.getStatusLabel({value: approvalStatus, statuses: invoiceApprovalStatuses, t});
  },

  getInvoiceSourceTypeTag: (value) => {
    return value
      ? (
        <Tag style={{
          backgroundColor: '#F3F3F4',
          borderColor: '#CDD0D5',
          borderRadius: 2,
          color: '#9CA1AA',
          marginRight: 0
        }}>{value}</Tag>
      )
      : '';
  },

  isInvoiceSettled: (status) => {
    return [
      invoicePaymentStatusConstants.PAID,
      invoicePaymentStatusConstants.PAID_OUTSIDE,
      invoicePaymentStatusConstants.PROCESSING
    ].includes(status);
  },

  isExpenseInvoiceType: (expense) => expense?.expense_type === expenseTypesConstants.INVOICE,

  isAccountingInvoice: (invoice) => invoice && invoice?.accounting_invoice_id,

  getInvoiceDetailsLink: (id) => {
    if (!id) return '#';
    return `${routes.invoicesList}/${id}`;
  },

  getInvoicePaymentDetailsLink: (id) => {
    if (!id) return '#';
    return `${routes.invoicesList}/${id}#payment`;
  },

  isUnassignedInvoice: (invoice) => {
    return invoice?.approval_status === invoiceApprovalStatusConstants.UNASSIGNED;
  },

  getInvoiceVendorName: (invoice) => {
    if (!invoice) return '';
    return invoice?.vendor_local_name || invoice?.vendor || '';
  }
}
